import { getConsent, hasConsent } from "../privacy/consent.js";

const BUFFER_LIMIT = 500;
const KEY_BLOCKLIST = [
  "email",
  "name",
  "phone",
  "password",
  "token",
  "secret",
  "address",
];

const state = {
  enabled: false,
  endpoint: "",
  app: "unknown",
  debug: false,
  seq: 0,
  sessionId: null,
  buffer: [],
};

function canUseWindow() {
  return typeof window !== "undefined";
}

function nowIso() {
  return new Date().toISOString();
}

function resolveSessionId() {
  if (!canUseWindow()) {
    return `server-${Date.now().toString(36)}`;
  }
  const globalState = window.__CEDRUM_ANALYTICS_STATE__ || {};
  if (typeof globalState.sessionId === "string" && globalState.sessionId.trim()) {
    return globalState.sessionId.trim();
  }
  const randomPart =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  const sessionId = `sess_${randomPart}`;
  window.__CEDRUM_ANALYTICS_STATE__ = {
    ...globalState,
    sessionId,
  };
  return sessionId;
}

function sanitizePathname(pathname) {
  const raw = String(pathname || "").split("?")[0].split("#")[0];
  if (!raw) return "/";
  return raw
    .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/gi, ":id")
    .replace(/\/\d{4,}(?=\/|$)/g, "/:id");
}

function sanitizeStringValue(value) {
  const trimmed = String(value || "").trim();
  if (!trimmed) return "";
  if (trimmed.includes("@")) return "[redacted]";
  if (/^https?:\/\//i.test(trimmed)) return "[redacted]";
  return trimmed.slice(0, 180);
}

function shouldDropKey(key) {
  const normalized = String(key || "").toLowerCase();
  if (!normalized) return true;
  return KEY_BLOCKLIST.some((fragment) => normalized.includes(fragment));
}

function sanitizeProps(props = {}) {
  if (!props || typeof props !== "object" || Array.isArray(props)) return {};
  const out = {};
  const entries = Object.entries(props);
  for (let index = 0; index < entries.length; index += 1) {
    const [rawKey, rawValue] = entries[index];
    const key = String(rawKey || "").trim();
    if (!key || shouldDropKey(key)) continue;

    const valueType = typeof rawValue;
    if (valueType === "string") {
      const safe = sanitizeStringValue(rawValue);
      if (!safe) continue;
      out[key] = safe;
      continue;
    }

    if (valueType === "number") {
      if (!Number.isFinite(rawValue)) continue;
      out[key] = Math.round(rawValue * 100) / 100;
      continue;
    }

    if (valueType === "boolean") {
      out[key] = rawValue;
      continue;
    }
  }
  return out;
}

function sanitizeEventName(name) {
  const normalized = String(name || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_.-]+/g, "_");
  return normalized.slice(0, 80);
}

function resolvePathname() {
  if (!canUseWindow()) return "/";
  return sanitizePathname(window.location?.pathname || "/");
}

function resolveDiagnosticsConsent() {
  return hasConsent("diagnostics", getConsent());
}

function pushToBuffer(payload) {
  state.buffer.push(payload);
  if (state.buffer.length > BUFFER_LIMIT) {
    state.buffer.splice(0, state.buffer.length - BUFFER_LIMIT);
  }
  if (!canUseWindow()) return;
  window.__CEDRUM_ANALYTICS_EVENTS__ = [...state.buffer];
}

function emitDebugEvent(payload) {
  if (!canUseWindow()) return;
  try {
    window.dispatchEvent(
      new CustomEvent("cedrum:analytics", {
        detail: payload,
      }),
    );
  } catch {
    // Ignore event dispatch errors in hardened contexts.
  }
}

function sendToEndpoint(payload) {
  const endpoint = String(state.endpoint || "").trim();
  if (!endpoint) return;
  const body = JSON.stringify(payload);

  if (
    canUseWindow() &&
    typeof navigator !== "undefined" &&
    typeof navigator.sendBeacon === "function"
  ) {
    try {
      const blob = new Blob([body], { type: "application/json" });
      navigator.sendBeacon(endpoint, blob);
      return;
    } catch {
      // Fall through to fetch.
    }
  }

  if (!canUseWindow() || typeof fetch !== "function") {
    return;
  }

  void fetch(endpoint, {
    method: "POST",
    headers: {
      "content-type": "application/json",
    },
    body,
    keepalive: true,
    credentials: "omit",
    mode: "cors",
  }).catch(() => {
    // Best-effort only.
  });
}

export function enableAnalytics({
  app = "unknown",
  endpoint = "",
  debug = false,
} = {}) {
  state.enabled = true;
  state.app = String(app || "unknown").trim() || "unknown";
  state.endpoint = String(endpoint || "").trim();
  state.debug = Boolean(debug);
  state.sessionId = resolveSessionId();

  return () => {
    disableAnalytics();
  };
}

export function disableAnalytics() {
  state.enabled = false;
}

export function isAnalyticsEnabled() {
  return Boolean(state.enabled);
}

export function trackAnalyticsEvent(eventName, props = {}, { essential = false } = {}) {
  if (!state.enabled) return false;
  if (!essential && !resolveDiagnosticsConsent()) {
    return false;
  }

  const normalizedEvent = sanitizeEventName(eventName);
  if (!normalizedEvent) return false;

  state.seq += 1;
  const payload = {
    event: normalizedEvent,
    app: state.app,
    ts: nowIso(),
    seq: state.seq,
    sessionId: state.sessionId || resolveSessionId(),
    path: resolvePathname(),
    props: sanitizeProps(props),
  };

  pushToBuffer(payload);
  emitDebugEvent(payload);
  sendToEndpoint(payload);

  if (state.debug && canUseWindow()) {
    // eslint-disable-next-line no-console
    console.debug("[analytics]", payload.event, payload.props);
  }
  return true;
}

export function createAnalyticsPerfTimer(eventName, baseProps = {}) {
  const startedAt =
    typeof performance !== "undefined" && typeof performance.now === "function"
      ? performance.now()
      : Date.now();
  let ended = false;

  return {
    end(extraProps = {}) {
      if (ended) return null;
      ended = true;
      const endedAt =
        typeof performance !== "undefined" && typeof performance.now === "function"
          ? performance.now()
          : Date.now();
      const durationMs = Math.max(0, endedAt - startedAt);
      trackAnalyticsEvent(eventName, {
        ...baseProps,
        ...extraProps,
        durationMs,
      });
      return durationMs;
    },
  };
}

export function getAnalyticsBuffer() {
  return [...state.buffer];
}
